import React from "react";
import { Phone, Mail, MapPin, Clock, ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";
import StarRating from "./StarRating";
import { OPEN_COOKIE_SETTINGS_EVENT } from "./CookieConsent";
import { companyInfo, footerLinks } from "../data/mock";
import { locations } from "../data/locations";
import { serviceIndex } from "../data/services";

const extraLinks = [
  { label: "Zakelijke schoonmaak", to: "/zakelijk" },
  { label: "Wat kost een glazenwasser?", to: "/kosten-glazenwasser" },
];

const Footer = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const openCookieSettings = () => window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));

  return (
    <footer className="bg-slate-900 text-white" role="contentinfo">
      <div className="bg-gradient-to-r from-sky-600 to-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">Wilt u ook een tevreden klant worden?</h3>
              <p className="text-white/80">Bereken uw prijs in 60 seconden of laat u vrijblijvend terugbellen.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#calculator" className="inline-flex items-center justify-center px-8 py-4 bg-white text-sky-600 font-semibold rounded-xl hover:bg-sky-50 transition-colors shadow-lg" data-testid="footer-cta-calculator">
                Bereken Uw Prijs
              </a>
              <a href={`tel:${companyInfo.phoneClean}`} className="inline-flex items-center justify-center px-8 py-4 bg-white/20 backdrop-blur-md text-white font-semibold rounded-xl hover:bg-white/30 transition-colors border border-white/30" data-testid="footer-cta-phone">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          <div className="lg:col-span-2">
            <img
              src="https://customer-assets.emergentagent.com/job_sparkling-cleven/artifacts/unn65btx_logo-cleven-cleaning.png"
              alt="Cleven Cleaning Logo"
              className="h-14 w-auto mb-6"
            />
            <p className="text-slate-400 mb-6 max-w-md">
              Professionele glazenwasser en schoonmaakdiensten voor particulieren en bedrijven in {companyInfo.location}. Altijd vooraf op de hoogte, altijd tevreden.
            </p>
            <div className="flex items-center space-x-2">
              <span className="text-amber-400 font-bold text-lg">{companyInfo.rating}</span>
              <StarRating rating={companyInfo.rating} size="w-4 h-4" emptyClass="text-slate-600" />
              <span className="text-slate-400">({companyInfo.reviewCount} Google reviews)</span>
            </div>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-6">Diensten</h4>
            <ul className="space-y-3">
              {serviceIndex.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} data-testid={`footer-service-link-${s.slug}`} className="text-slate-400 hover:text-white transition-colors">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-6">Werkgebied</h4>
            <ul className="space-y-3">
              {locations.map((loc) => (
                <li key={loc.slug}>
                  <Link to={`/glazenwasser-${loc.slug}`} data-testid={`footer-location-link-${loc.slug}`} className="text-slate-400 hover:text-white transition-colors">
                    Glazenwasser {loc.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${companyInfo.phoneClean}`} className="flex items-center text-slate-400 hover:text-white transition-colors">
                  <Phone className="w-5 h-5 mr-3 text-sky-500" />
                  {companyInfo.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${companyInfo.email}`} className="flex items-center text-slate-400 hover:text-white transition-colors">
                  <Mail className="w-5 h-5 mr-3 text-sky-500" />
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-center text-slate-400">
                <MapPin className="w-5 h-5 mr-3 text-sky-500" />
                {companyInfo.location}
              </li>
              <li className="flex items-center text-slate-400">
                <Clock className="w-5 h-5 mr-3 text-sky-500" />
                Ma - Vr: 08:00 - 18:00
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-6">Links</h4>
            <ul className="space-y-4">
              {extraLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} data-testid={`footer-extra-link-${link.to.slice(1)}`} className="text-slate-400 hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
              {footerLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.to} data-testid={`footer-link-${index}`} className="text-slate-400 hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <button onClick={openCookieSettings} className="text-slate-400 hover:text-white transition-colors" data-testid="footer-cookie-settings-btn">
                  Cookie-instellingen
                </button>
              </li>
              <li>
                <span className="text-slate-500">KvK: {companyInfo.kvk}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm text-center sm:text-left">
              © {new Date().getFullYear()} {companyInfo.name}. Alle rechten voorbehouden.
            </p>
            <button onClick={scrollToTop} className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors" data-testid="footer-scroll-top-btn">
              <span className="text-sm">Terug naar boven</span>
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
